import React, { useEffect, useState, useCallback } from 'react';
import {
    Card, CardContent, Divider,
    Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow,
    TextField, Stack, CardHeader,
    Checkbox, Menu, MenuItem, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, Typography, IconButton, Chip, Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SearchIcon from '@mui/icons-material/Search';
import ExportDropdownButton from '../../../components/Shared/ExportDropdownButton'
import { api } from '../../../services/Main/Api';
import { useSnackbar } from 'notistack';
import PaginationPage from '../../../components/PaginationPage/PaginationPage';
import RentContractForm from "./RentContractForm";
import {Add} from "@mui/icons-material";
import {getCondoId, getCondoType} from "../../../services/Main/AuthStorage";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import moment from "moment";
import {CalendarIcon} from "@mui/x-date-pickers";
import {blue, deepPurple, green, grey, red} from '@mui/material/colors';
const colorMap = {
    purple:    '#a259d9',
    orange:    '#f4a261',
    blue:      '#2196f3',
    green:     '#43a047',
    red:       '#e63946',
    yellow:    '#ffd600',
    teal:      '#26c6da',
    pink:      '#ec407a',
    brown:     '#8d6e63',
    gray:      '#bdbdbd',
    indigo:    '#3f51b5',
    cyan:      '#00bcd4',
    lime:      '#cddc39',
    amber:     '#ffb300',
    deepOrange:'#ff7043',
    deepPurple:'#7c43bd',
    lightBlue: '#29b6f6',
    lightGreen:'#8bc34a',
    gold:      '#ffd700',
    silver:    '#c0c0c0',
};

const RentContractList = () => {
    const [search, setSearch] = useState('');
    const [contracts, setContracts] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [showSearch, setShowSearch] = useState(false);
    const [openFormRent, setOpenFormRent] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const openMenu = Boolean(anchorEl);
    const condoId = getCondoId()
    useEffect(() => {
        api.get('rent_contract/v2/', { params: { search, page, page_size: 50, unit__condo_id: condoId } })
            .then(response => {
                setContracts(response.data);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setSelectedIds([]);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [search, page]);

    const handleSelectAll = (event) => {
        const results = contracts?.results || [];
        setSelectedIds(event.target.checked ? results.map(c => c.id) : []);
    };

    const handleSelectOne = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]);
    };

    const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleDeleteClick = () => { setConfirmOpen(true); handleMenuClose(); };

    const handleDeleteConfirmed = async () => {
        try {
            await Promise.all(selectedIds.map(id => api.delete(`rent_contract/${id}/`)));
            const updated = contracts.results.filter(c => !selectedIds.includes(c.id));
            setContracts(prev => ({ ...prev, results: updated }));
            enqueueSnackbar("Contrato(s) deletado(s) com sucesso!", { variant: 'success' });
        } catch (error) {
            console.error('Erro ao deletar contrato:', error);
            enqueueSnackbar(`Erro ao deletar contrato: ${error}`, { variant: 'error' });
        } finally {
            setSelectedIds([]);
            setConfirmOpen(false);
        }
    };

    const buildExportParams = useCallback(() => {
        const params = { condo: String(getCondoId()) }
        if (search) params.q = search
        return params
    }, [search])

    const isSelected = (id) => selectedIds.includes(id);
    const allSelected = contracts.results && contracts.results.length > 0 && selectedIds.length === contracts.results.length;

    return (
        <>
            <Card sx={{ margin: 2 }}>
                <CardHeader title={`Contratos (${contracts?.count || 0})`} />
                <Divider sx={{ mb: 2 }} />

                <Stack direction="row" sx={{ px: 2 }} spacing={2} alignItems="center">
                    <Button
                        variant={'contained'}
                        size={'small'}
                        onClick={() => {
                            setOpenFormRent(true);
                        }}
                    >
                        <Add /> { `Criar Contrato` }
                    </Button>
                    {showSearch ? (
                        <TextField
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Pesquisar..."
                            autoFocus
                            onBlur={() => !search.trim() && setShowSearch(false)}
                            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} /> }}
                        />
                    ) : (
                        <IconButton onClick={() => setShowSearch(true)}>
                            <SearchIcon />
                        </IconButton>
                    )}

                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<KeyboardArrowDownIcon />}
                        onClick={handleMenuClick}
                    >
                        Ações {selectedIds.length > 1 ? `(${selectedIds.length} itens)` : selectedIds.length === 1 ? `(${selectedIds.length} item)` : null}
                    </Button>

                    <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
                        <MenuItem onClick={handleDeleteClick} disabled={selectedIds.length === 0}>
                            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                            Excluir selecionados
                        </MenuItem>
                    </Menu>

                    <ExportDropdownButton
                        endpoint="rent-contract/export/"
                        buildParams={buildExportParams}
                        defaultFilename="contratos_export"
                    />
                </Stack>

                <CardContent>
                    {loading ? (
                        <Typography variant="body2">Carregando contratos...</Typography>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={allSelected} onChange={handleSelectAll} />
                                        </TableCell>
                                        <TableCell><strong>#</strong></TableCell>
                                        <TableCell><strong>Nome</strong></TableCell>
                                        <TableCell><strong>Gerar fatura</strong></TableCell>
                                        <TableCell><strong>Vencimento</strong></TableCell>
                                        {getCondoType() === 'RENT' &&
                                            <TableCell><strong>Repasse</strong></TableCell> }
                                        <TableCell><strong>Iniciou em</strong></TableCell>
                                        {/*<TableCell><strong>Beneficiários</strong></TableCell>*/}
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {contracts?.results?.map((contract) => (
                                        <TableRow key={contract.id} hover
                                                  onClick={() => navigate(`/contratos/${contract.id}`)}
                                                  sx={{cursor: 'pointer'}}
                                        >
                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox checked={isSelected(contract.id)} onChange={() => handleSelectOne(contract.id)} />
                                            </TableCell>
                                            <TableCell>{contract.number}</TableCell>
                                            <TableCell>{contract.name}</TableCell>

                                            <TableCell>
                                                <Chip
                                                    label={contract.generate_bankslip_day > 0 ? `Todo dia ${contract.generate_bankslip_day}`: `Gerar manualmente`}
                                                    size="small"
                                                    variant={contract.generate_bankslip_day > 0 ? "filled":"outlined"}
                                                    color={contract.generate_bankslip_day > 0 ? "default" : "warning"}
                                                    icon={
                                                        <CalendarTodayIcon sx={{ fontSize: 16 }} />
                                                    }
                                                    sx={{

                                                        maxWidth: 300,
                                                        '& .MuiChip-icon': {
                                                            color: 'default',
                                                            marginLeft: '4px'
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={contract.bankslip_due_day > 0 ? `Todo dia ${contract.bankslip_due_day}`: `Sem infomação`}
                                                    size="small"

                                                    color="default"
                                                    icon={
                                                        <CalendarTodayIcon sx={{ fontSize: 16 }} />
                                                    }
                                                    sx={{

                                                        maxWidth: 300,
                                                        '& .MuiChip-icon': {
                                                            color: 'default',
                                                            marginLeft: '4px'
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            {getCondoType() === 'RENT' &&  <TableCell>
                                                <Tooltip title={contract.transfer_day && `Será realizado todo dia ${contract.transfer_day} do mês`}>
                                                    <Chip
                                                        label={contract.auto_transfer}
                                                        size="small"
                                                        variant="outlined"
                                                        color={ contract.auto_transfer === 'Manual' ? "warning" : "success"}

                                                        sx={{

                                                            maxWidth: 300,
                                                            '& .MuiChip-icon': {
                                                                color: 'default',
                                                                marginLeft: '4px'
                                                            }
                                                        }}
                                                    />
                                                </Tooltip>
                                            </TableCell> }

                                            {/*<TableCell>{contract.signed_recipients?.join(', ')}</TableCell>*/}

                                            <TableCell>
                                                <Chip
                                                    label={moment(contract.start_date).format('DD/MM/YYYY')}
                                                    size="small"

                                                    icon={<CalendarIcon fontSize="small" />}
                                                    variant="outlined"/>

                                            </TableCell>

                                            <TableCell>
                                                <Tooltip title={`Criado em ${moment(contract.created_at).format('DD/MM/YYYY [às] HH:mm:ss')}`}>
                                                    <Chip
                                                        label={contract.status === "active" ? "Ativo" : contract.status === "inactive" ? "Encerrado" : "Pendente"}
                                                        size="small"
                                                        variant="outlined"
                                                        color={ contract.status === 'pending' ? "warning" : contract.status === 'active' ? "success" : 'error'}

                                                        sx={{

                                                            maxWidth: 300,
                                                            '& .MuiChip-icon': {
                                                                color: 'default',
                                                                marginLeft: '4px'
                                                            }
                                                        }}
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={contract.fine_and_interest ? contract.fine_and_interest : "Multa e juros não foi informado"}>
                                                    <MonetizationOnOutlinedIcon  sx={{color: contract.fine_and_interest ? blue[300] : grey[400]}} />
                                                </Tooltip>
                                                <Tooltip title={contract.send_email ? `Envio de e-mail está ativo` : 'Envio de e-mail está desativado'}>
                                                    <MarkEmailReadOutlinedIcon  sx={{color: contract.send_email ? red["300"] : grey[400]}} />
                                                </Tooltip>
                                                <Tooltip title={contract.send_whatsapp ? `Envio para o Whatsapp está ativo` : 'Envio para o Whatsapp está desativado'}>
                                                    <WhatsAppIcon color={ contract.send_email ? blue[500] : "action"} sx={{color: contract.send_email ? green[300] : grey[400]}} />
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {contracts.results?.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">Nenhum contrato encontrado.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <PaginationPage
                data={{ ...contracts, page }}
                onChange={(newPage) => setPage(newPage)}
            />

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja deletar {selectedIds.length} {selectedIds.length === 1 ? 'item' : 'itens'}?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} size="small">Cancelar</Button>
                    <Button onClick={handleDeleteConfirmed} color="error" variant="contained" size="small">Deletar</Button>
                </DialogActions>
            </Dialog>

            { openFormRent &&
                <RentContractForm
                    closeForm={()=>{
                        setOpenFormRent(false)
                    }}
                /> }
        </>
    );
};

export default RentContractList;